import { NetworkService } from '@damofire/data-angular-commons';
import { captureMessage } from '@sentry/angular';
import { ChangeTyped, Dto, IndexedService, logger } from '@damofire/data-commons';
import { OPERATION, WarehouseSyncEvent } from '../../des.dto';

export abstract class RemoteService<T extends Dto> {
  protected constructor(
    protected readonly store: string,
    protected readonly idbService: IndexedService,
    protected readonly networkService: NetworkService,
  ) {}
  abstract add(division: string, data: T[]): Promise<void>;
  abstract update(division: string, data: T[]): Promise<void>;
  abstract delete(division: string, data: T[]): Promise<void>;
  async saveAll(event: WarehouseSyncEvent): Promise<void> {
    for (const c of event.changes) {
      if (this.networkService.connected) {
        try {
          await this.saveOnlineChangeOperation(event.division, c);
        } catch (e) {
          logger.error('Something horrible happened inside saveOnlineChangeOperation, fallback to saveOfflineChangeOperation to try to save it.', { error: e, division: event.division, category: 'sync' });
          captureMessage(`Something horrible happened inside saveOnlineChangeOperation, fallback to saveOfflineChangeOperation to try to save it. [${e}]`, 'error');
          await this.saveOfflineChangeOperation(event.division, c);
        }
      } else {
        await this.saveOfflineChangeOperation(event.division, c);
      }
    }
  }

  private async saveOnlineChangeOperation(division: string, data: ChangeTyped<T & { syncId?: string }>): Promise<void> {
    const { changeType, syncId, ...dto } = data;

    switch (changeType) {
      case 'added':
        await this.add(division, [dto as T]);
        break;
      case 'removed':
        await this.delete(division, [dto as T]);
        break;
      default:
        await this.update(division, [dto as T]);
    }
  }

  private async saveOfflineChangeOperation(division: string, change: ChangeTyped<T>): Promise<void> {
    const operationsStore = `${this.store}-operations`;
    const { changeType, ...data } = change;
    switch (changeType) {
      case 'added':
        await this.idbService.add(operationsStore, { division: { _id: division }, _id: data._id, data, operation: OPERATION.ADD });
        break;
      case 'removed':
        if (await this.idbService.exists(operationsStore, [division, data._id, OPERATION.ADD])) {
          await this.idbService.delete(operationsStore, [division, data._id, OPERATION.ADD]);
        } else {
          await this.idbService.add(operationsStore, { ...data, division: { _id: division }, operation: OPERATION.DELETE });
        }
        await this.idbService.delete(operationsStore, [division, data._id, OPERATION.UPDATE]);
        break;
      default:
        if (await this.operationDoesntAlreadyExists(operationsStore, division, data._id)) {
          await this.idbService.add(operationsStore, { division: { _id: division }, _id: data._id, data, operation: OPERATION.UPDATE });
        }
    }
  }

  private async operationDoesntAlreadyExists(operationsStore: string, division: string, id: string | number): Promise<boolean> {
    const matchingAdd = await this.idbService.exists(operationsStore, [division, id, OPERATION.ADD]);
    const matchingUpdate = await this.idbService.exists(operationsStore, [division, id, OPERATION.UPDATE]);
    return !matchingAdd && !matchingUpdate;
  }
}
